
import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiUrl } from '../lib/apiBase';

const AuthContext = createContext();
const USER_STORAGE_KEY = import.meta.env.VITE_USER_STORAGE_KEY || 'curelo_admin_user';
const TOKEN_STORAGE_KEY = import.meta.env.VITE_TOKEN_STORAGE_KEY || 'curelo_admin_token';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = sessionStorage.getItem(USER_STORAGE_KEY);
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_STORAGE_KEY) || '');

    const getAuthHeaders = () => (
        token
            ? { Authorization: `Bearer ${token}` }
            : {}
    );

    const login = async (username, password) => {
        try {
            const response = await fetch(apiUrl('/api/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });

            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
                setToken(data.token || '');
                sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
                if (data.token) {
                    sessionStorage.setItem(TOKEN_STORAGE_KEY, data.token);
                } else {
                    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
                }
                return { success: true };
            }

            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Invalid credentials' };
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, message: 'Could not connect to the authentication server. Please ensure the API is running.' };
        }
    };

    const logout = () => {
        setUser(null);
        setToken('');
        sessionStorage.removeItem(USER_STORAGE_KEY);
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch(apiUrl('/api/users'));
            if (response.ok) {
                return await response.json();
            }
            return [];
        } catch (error) {
            console.error('Error fetching users:', error);
            return [];
        }
    };

    const addUser = async (newUser) => {
        try {
            const response = await fetch(apiUrl('/api/users'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify(newUser),
            });
            return response.ok;
        } catch (error) {
            console.error('Error adding user:', error);
            return false;
        }
    };

    const deleteUser = async (username) => {
        try {
            const response = await fetch(apiUrl(`/api/users/${username}`), {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            return response.ok;
        } catch (error) {
            console.error('Error deleting user:', error);
            return false;
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            login,
            logout,
            fetchUsers,
            addUser,
            deleteUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};
